define('OutSystems/ClientRuntime/modules/Database/WebSQL/WebSQLTransaction',["require", "exports", "tslib", "../../../Logger", "../AutoNumber", "../DBDataType", "../TableInfoUtils", "./WebSQLTransactionMode"], function (require, exports, tslib_1, Logger, AutoNumber_1, DBDataType_1, TableInfoUtils, WebSQLTransactionMode_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    var NEWLINE = "\r\n";
    var sqlTypeMap = new Map();
    sqlTypeMap.set(DBDataType_1.default.BINARY_DATA, "BLOB");
    sqlTypeMap.set(DBDataType_1.default.BOOLEAN, "BOOLEAN");
    sqlTypeMap.set(DBDataType_1.default.DATE, "DATE");
    sqlTypeMap.set(DBDataType_1.default.DATE_TIME, "DATETIME");
    sqlTypeMap.set(DBDataType_1.default.DECIMAL, "REAL");
    sqlTypeMap.set(DBDataType_1.default.INTEGER, "INTEGER");
    sqlTypeMap.set(DBDataType_1.default.LONGINTEGER, "BIGINT");
    sqlTypeMap.set(DBDataType_1.default.TEXT, "TEXT");
    sqlTypeMap.set(DBDataType_1.default.TIME, "TIME");
    function getType(type) {
        var _a;
        return (_a = sqlTypeMap.get(type)) !== null && _a !== void 0 ? _a : "";
    }
    var TRANSACTION_ID_MAX = 1000000000000;
    var WRITE_TRANSACTION_REQUIRED_ERROR = "The transaction is read-only";
    var UPGRADE_TRANSACTION_REQUIRED_ERROR = "The database is not running a version change transaction";
    var WebSQLTransaction = (function () {
        function WebSQLTransaction(allowTraces, transaction, mode) {
            this.allowTraces = allowTraces;
            this.transaction = transaction;
            this.mode = mode;
            WebSQLTransaction.lastTransactionId = (WebSQLTransaction.lastTransactionId + 1) % TRANSACTION_ID_MAX;
            this._transactionId = WebSQLTransaction.lastTransactionId + 1;
            if (this.allowTraces) {
                Logger.trace(this, "Starting transaction " + this.transactionId);
            }
        }
        Object.defineProperty(WebSQLTransaction.prototype, "transactionId", {
            get: function () {
                return this._transactionId;
            },
            enumerable: false,
            configurable: true
        });
        WebSQLTransaction.prototype.executeQuery = function (body) {
            return body(this.transaction);
        };
        WebSQLTransaction.prototype.getColumnDeclaration = function (table, column) {
            var typeWithOptions = "" + getType(column.type);
            var primaryKeyDeclaration;
            if (column.isPrimaryKey) {
                if (table.idIsAutoNumber === AutoNumber_1.default.YesIfEmpty || table.idIsAutoNumber === AutoNumber_1.default.Yes) {
                    if (column.type === DBDataType_1.default.LONGINTEGER) {
                        typeWithOptions = "" + getType(DBDataType_1.default.INTEGER);
                    }
                    typeWithOptions += " PRIMARY KEY AUTOINCREMENT NOT NULL";
                    return { declaration: "\"" + column.columnName + "\" " + typeWithOptions };
                }
                primaryKeyDeclaration = "\"" + column.columnName + "\"";
            }
            if (column.length) {
                typeWithOptions += "(" + column.length + (column.decimals ? "," + column.decimals : "") + ")";
            }
            if (column.serializedDefault !== undefined) {
                var defaultValue = column.serializedDefault;
                if (typeof defaultValue === "string") {
                    defaultValue = "'" + defaultValue.replace("'", "''") + "'";
                }
                typeWithOptions += " DEFAULT " + defaultValue;
            }
            return { declaration: "\"" + column.columnName + "\" " + typeWithOptions, primaryKeyDeclaration: primaryKeyDeclaration };
        };
        WebSQLTransaction.prototype.createTable = function (table) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Creating table '" + table.tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Upgrade) {
                    var error = new Error(UPGRADE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error creating table '" + table.tableName + "': " + error.message, error);
                    return reject(error);
                }
                var successCallback = function (transaction, resultSet) {
                    _this.trace("Table '" + table.tableName + "' created with success");
                    resolve();
                };
                var errorCallback = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error creating table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                var columnDeclarations = table.attributes.map(function (attribute) { return _this.getColumnDeclaration(table, attribute); });
                var primaryKeyDeclarations = columnDeclarations
                    .filter(function (declaration) { return !!declaration.primaryKeyDeclaration; })
                    .map(function (declaration) { return declaration.primaryKeyDeclaration; });
                var tableDeclaration = columnDeclarations.map(function (declaration) { return declaration.declaration; });
                if (primaryKeyDeclarations.length > 0) {
                    tableDeclaration.push("PRIMARY KEY (" + primaryKeyDeclarations.join(",") + ")");
                }
                _this.transaction.executeSql("CREATE TABLE \"" + table.tableName + "\" (" + tableDeclaration.join(",") + ")", null, successCallback, errorCallback);
            });
        };
        WebSQLTransaction.prototype.deleteTableIfExists = function (table) {
            var _this = this;
            var tableName;
            if (typeof table === "string") {
                tableName = table;
            }
            else {
                tableName = table.tableName;
            }
            return new Promise(function (resolve, reject) {
                _this.trace("Deleting table '" + tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Upgrade) {
                    var error = new Error(UPGRADE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error deleting table '" + tableName + "'", error);
                    return reject(error);
                }
                var successCallback = function (transaction, resultSet) {
                    _this.trace("Table '" + tableName + "' deleted with success");
                    resolve();
                };
                var errorCallback = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error deleting table '" + tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("DROP TABLE IF EXISTS \"" + tableName + "\"", null, successCallback, errorCallback);
            });
        };
        WebSQLTransaction.prototype.renameTable = function (tableName, newTableName) {
            var _this = this;
            return new Promise(function (resolve, reject) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var error, successCallback, errorCallback;
                var _this = this;
                return tslib_1.__generator(this, function (_a) {
                    this.trace("Renaming table '" + tableName + "'");
                    if (this.mode < WebSQLTransactionMode_1.default.Upgrade) {
                        error = new Error(UPGRADE_TRANSACTION_REQUIRED_ERROR);
                        this.trace("Error renaming table '" + tableName + "'", error);
                        return [2, reject(error)];
                    }
                    successCallback = function (transaction, resultSet) {
                        _this.trace("Table '" + tableName + "' renamed to '" + newTableName + "' with success");
                        resolve();
                    };
                    errorCallback = function (transaction, sqlError) {
                        var error = new Error(sqlError.message.toString());
                        _this.trace("Error renaming table '" + tableName + "'", error);
                        reject(error);
                        return false;
                    };
                    this.transaction.executeSql("ALTER TABLE \"" + tableName + "\" RENAME TO \"" + newTableName + "\"", null, successCallback, errorCallback);
                    return [2];
                });
            }); });
        };
        WebSQLTransaction.prototype.existsTable = function (table) {
            var _this = this;
            var tableName;
            if (typeof table === "string") {
                tableName = table;
            }
            else {
                tableName = table.tableName;
            }
            return new Promise(function (resolve, reject) {
                _this.trace("Checking if table '" + tableName + "' exists");
                var callback = function (transaction, resultSet) {
                    _this.trace("Table '" + tableName + "' checked with success");
                    resolve(resultSet.rows.length > 0);
                };
                var callbackError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error checking if table '" + tableName + "' exists", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("SELECT * FROM sqlite_master WHERE type = 'table' AND UPPER(name) = UPPER(\"" + tableName + "\")", null, callback, callbackError);
            });
        };
        WebSQLTransaction.prototype.addColumn = function (table, column) {
            var _this = this;
            return new Promise(function (resolve, reject) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var columnObjectSerialized, error, successCallback, errorCallback, addColumnSuccessCallback;
                var _this = this;
                return tslib_1.__generator(this, function (_a) {
                    columnObjectSerialized = Logger.serializeObjectForLog(column);
                    this.trace("Adding column '" + columnObjectSerialized + "' on table '" + table.tableName + "'");
                    if (this.mode < WebSQLTransactionMode_1.default.Upgrade) {
                        error = new Error(UPGRADE_TRANSACTION_REQUIRED_ERROR);
                        this.trace("Error adding column '" + columnObjectSerialized + "'", error);
                        return [2, reject(error)];
                    }
                    successCallback = function (transaction, resultSet) {
                        _this.trace("Column '" + columnObjectSerialized + "' added with success");
                        resolve();
                    };
                    errorCallback = function (transaction, sqlError) {
                        var error = new Error(sqlError.message.toString());
                        _this.trace("Error adding column '" + columnObjectSerialized + "'", error);
                        reject(error);
                        return false;
                    };
                    addColumnSuccessCallback = function (transaction, resultSet) {
                        transaction.executeSql("UPDATE \"" + table.tableName + "\" SET \"" + column.columnName + "\" = ?", [column.serializedDefault], successCallback, errorCallback);
                    };
                    this.transaction.executeSql("ALTER TABLE \"" + table.tableName + "\" ADD " + this.getColumnDeclaration(table, column).declaration, null, addColumnSuccessCallback, errorCallback);
                    return [2];
                });
            }); });
        };
        WebSQLTransaction.prototype.existsColumn = function (tableName, columnName) {
            var _this = this;
            return new Promise(function (resolve) {
                _this.trace("Checking if column '" + columnName + "' exists on table '" + tableName + "'");
                var callback = function () {
                    _this.trace("Column '" + columnName + "' exists on table '" + tableName + "'");
                    resolve(true);
                };
                var callbackError = function () {
                    _this.trace("Column '" + columnName + "' does not exist on table '" + tableName + "'");
                    resolve(false);
                    return false;
                };
                _this.transaction.executeSql("SELECT \"" + tableName + "\".\"" + columnName + "\" FROM \"" + tableName + "\" LIMIT 0", null, callback, callbackError);
            });
        };
        WebSQLTransaction.prototype.getRecord = function (table, key) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Getting record with keys '" + key + "' from table '" + table.tableName + "'");
                var wrappedKey = WebSQLTransaction.wrapKeyIfNeeded(key);
                var columnNames = table.attributes.map(function (att) { return "\"" + att.columnName + "\""; }).join(", ");
                var tableName = "\"" + table.tableName + "\"";
                var primaryKeys = table.attributes.filter(function (att) { return att.isPrimaryKey; });
                var argList = tslib_1.__spreadArray([], wrappedKey);
                var keysClause = primaryKeys.map(function (att) { return "\"" + att.columnName + "\" = ?"; }).join(" and ");
                var onSuccess = function (transaction, resultSet) {
                    _this.trace("Retrieved record with keys '" + key + "' from table '" + table.tableName + "' successfully");
                    var dbResult = WebSQLTransaction.convertSQLResultSetToDatabaseRecord(table, resultSet);
                    resolve(dbResult.length > 0 ? dbResult[0] : null);
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error retrieving record with keys '" + key + "' from table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("SELECT " + columnNames + " FROM " + tableName + " WHERE " + keysClause, argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.insertRecord = function (table, record) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Inserting record on table '" + table.tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Write) {
                    var error = new Error(WRITE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error inserting record on table '" + table.tableName + "'", error);
                    return reject(error);
                }
                var primaryKeys = table.attributes.filter(function (att) { return att.isPrimaryKey; });
                var columnNames = table.attributes.map(function (att) { return "\"" + att.columnName + "\""; }).join(", ");
                var placeholders = WebSQLTransaction.concatenateMultipleTimes("?", table.attributes.length, ", ");
                var tableName = "\"" + table.tableName + "\"";
                var argList = table.attributes.map(function (att) {
                    return record[att.columnName] === undefined ? null : record[att.columnName];
                });
                var onSuccess = function (transaction, resultSet) {
                    var _a, _b;
                    _this.trace("Inserted record on table '" + table.tableName + "' successfully");
                    var id = null;
                    if (table.idIsAutoNumber === AutoNumber_1.default.No ||
                        (table.idIsAutoNumber === AutoNumber_1.default.YesIfEmpty && record[(_a = primaryKeys[0]) === null || _a === void 0 ? void 0 : _a.columnName] !== null)) {
                        id = record[(_b = primaryKeys[0]) === null || _b === void 0 ? void 0 : _b.columnName];
                    }
                    else {
                        id = resultSet.insertId;
                    }
                    resolve(id);
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error inserting record on table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("INSERT INTO " + tableName + " (" + columnNames + ") VALUES (" + placeholders + ")", argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.updateRecord = function (table, record) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Updating record on table '" + table.tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Write) {
                    var error = new Error(WRITE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error updating record on table '" + table.tableName + "'", error);
                    return reject(error);
                }
                var notKeyColumns = table.attributes.filter(function (att) { return !att.isPrimaryKey; });
                var primaryKeys = table.attributes.filter(function (att) { return att.isPrimaryKey; });
                var columnsToUpdate = notKeyColumns.map(function (att) { return "\"" + att.columnName + "\" = ?"; }).join(", ");
                var whereClause = primaryKeys.map(function (att) { return "\"" + att.columnName + "\" = ?"; }).join(" and ");
                var tableName = "\"" + table.tableName + "\"";
                var argList = [];
                notKeyColumns.forEach(function (att) { return argList.push(record[att.columnName]); });
                primaryKeys.forEach(function (att) { return argList.push(record[att.columnName]); });
                var onSuccess = function (transaction, resultSet) {
                    if (resultSet.rowsAffected === 0) {
                        _this.trace("Record to update does not exist on table '" + table.tableName + "'");
                    }
                    else {
                        _this.trace("Updated record on table '" + table.tableName + "' successfully");
                    }
                    resolve();
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error updating record on table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("UPDATE " + tableName + " SET " + columnsToUpdate + " WHERE " + whereClause, argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.upsertRecord = function (table, record) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Adding/updating record on table '" + table.tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Write) {
                    var error = new Error(WRITE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error adding/updating record on table '" + table.tableName + "'", error);
                    return reject(error);
                }
                var primaryKeys = table.attributes.filter(function (att) { return att.isPrimaryKey; });
                var columnNames = table.attributes.map(function (att) { return "\"" + att.columnName + "\""; }).join(", ");
                var placeholders = WebSQLTransaction.concatenateMultipleTimes("?", table.attributes.length, ", ");
                var tableName = "\"" + table.tableName + "\"";
                var argList = table.attributes.map(function (att) { return record[att.columnName]; });
                var onSuccess = function (transaction, resultSet) {
                    var _a, _b;
                    _this.trace("Added/updated record on table '" + table.tableName + "' successfully");
                    var id = null;
                    if (table.idIsAutoNumber === AutoNumber_1.default.No ||
                        (table.idIsAutoNumber === AutoNumber_1.default.YesIfEmpty && record[(_a = primaryKeys[0]) === null || _a === void 0 ? void 0 : _a.columnName] !== null)) {
                        id = record[(_b = primaryKeys[0]) === null || _b === void 0 ? void 0 : _b.columnName];
                    }
                    else {
                        id = resultSet.insertId;
                    }
                    resolve(id);
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error adding/updating record on table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("INSERT OR REPLACE INTO " + tableName + " (" + columnNames + ") VALUES (" + placeholders + ")", argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.upsertAllRecords = function (table, records) {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var error, hasBinaryData, error_1;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            this.trace("Adding/updating " + (records === null || records === void 0 ? void 0 : records.length) + " record(s) on table '" + table.tableName + "'");
                            if (this.mode < WebSQLTransactionMode_1.default.Write) {
                                error = new Error(WRITE_TRANSACTION_REQUIRED_ERROR);
                                this.trace("Error adding/updating records on table '" + table.tableName + "'", error);
                                throw error;
                            }
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 6, , 7]);
                            hasBinaryData = table.attributes.some(function (attr) { return attr.type === DBDataType_1.default.BINARY_DATA; });
                            if (!hasBinaryData) return [3, 3];
                            return [4, this.innerUpsertAllSingle(table, records)];
                        case 2:
                            _a.sent();
                            return [3, 5];
                        case 3: return [4, this.innerUpsertAllBulk(table, records)];
                        case 4:
                            _a.sent();
                            _a.label = 5;
                        case 5:
                            this.trace("Added/updated " + (records === null || records === void 0 ? void 0 : records.length) + " record(s) on table '" + table.tableName + "' successfully");
                            return [3, 7];
                        case 6:
                            error_1 = _a.sent();
                            this.trace("Error adding/updating " + (records === null || records === void 0 ? void 0 : records.length) + " record(s) on table '" + table.tableName + "'", error_1);
                            throw error_1;
                        case 7: return [2];
                    }
                });
            });
        };
        WebSQLTransaction.prototype.innerUpsertAllSingle = function (table, records) {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var _i, records_1, record;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            this.trace("Adding/updating " + (records === null || records === void 0 ? void 0 : records.length) + " record(s) on table '" + table.tableName + "' one by one");
                            _i = 0, records_1 = records;
                            _a.label = 1;
                        case 1:
                            if (!(_i < records_1.length)) return [3, 4];
                            record = records_1[_i];
                            return [4, this.upsertRecord(table, record)];
                        case 2:
                            _a.sent();
                            _a.label = 3;
                        case 3:
                            _i++;
                            return [3, 1];
                        case 4: return [2];
                    }
                });
            });
        };
        WebSQLTransaction.prototype.innerUpsertAllBulk = function (table, records) {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var chunkSize, commands, chunkCount, _loop_1, this_1, i, currentChunk, _i, commands_1, command;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            chunkSize = Math.floor(WebSQLTransaction.MaxQueryParameters / table.attributes.length);
                            if (chunkSize === 0) {
                                return [2, this.innerUpsertAllSingle(table, records)];
                            }
                            commands = [];
                            chunkCount = Math.ceil(records.length / chunkSize);
                            this.trace("Adding/updating " + (records === null || records === void 0 ? void 0 : records.length) + " record(s) on table '" + table.tableName + "' in " + chunkCount + " chunk(s) of " + chunkSize + " record(s) each");
                            _loop_1 = function (i) {
                                var argList = [];
                                var recordCount = 0;
                                var _loop_2 = function () {
                                    var idx = i * chunkSize + recordCount;
                                    if (idx === records.length) {
                                        return "break";
                                    }
                                    var record = records[idx];
                                    table.attributes.forEach(function (att) { return argList.push(record[att.columnName]); });
                                    recordCount++;
                                };
                                while (recordCount < chunkSize) {
                                    var state_1 = _loop_2();
                                    if (state_1 === "break")
                                        break;
                                }
                                var query = this_1.createUpsertBulkQuery(table, recordCount);
                                commands.push({ query: query, argList: argList });
                            };
                            this_1 = this;
                            for (i = 0; i < chunkCount; i++) {
                                _loop_1(i);
                            }
                            currentChunk = 0;
                            _i = 0, commands_1 = commands;
                            _a.label = 1;
                        case 1:
                            if (!(_i < commands_1.length)) return [3, 4];
                            command = commands_1[_i];
                            this.trace("Adding/updating chunk " + ++currentChunk + " of " + chunkCount + " on table '" + table.tableName + "'");
                            return [4, this.wrapExecuteSqlOnPromise(command, table)];
                        case 2:
                            _a.sent();
                            _a.label = 3;
                        case 3:
                            _i++;
                            return [3, 1];
                        case 4: return [2];
                    }
                });
            });
        };
        WebSQLTransaction.prototype.wrapExecuteSqlOnPromise = function (command, table) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                var onSuccess = function (transaction, resultSet) {
                    _this.trace("Added/Updated bulk of records on table '" + table.tableName + "' successfully");
                    resolve();
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error adding/updating bulk of records on table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql(command.query, command.argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.createUpsertBulkQuery = function (table, recordCount) {
            var columnNames = table.attributes.map(function (att) { return "\"" + att.columnName + "\""; }).join(", ");
            var tableName = "\"" + table.tableName + "\"";
            var placeholders = "(" + WebSQLTransaction.concatenateMultipleTimes("?", table.attributes.length, ", ") + ")";
            var bulkPlaceholders = WebSQLTransaction.concatenateMultipleTimes(placeholders, recordCount, ", ");
            return "INSERT OR REPLACE INTO " + tableName + " (" + columnNames + ") VALUES " + bulkPlaceholders;
        };
        WebSQLTransaction.prototype.removeRecord = function (table, key) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Deleting record with key '" + key + "' from table '" + table.tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Write) {
                    var error = new Error(WRITE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error deleting record from table '" + table.tableName + "'", error);
                    return reject(error);
                }
                var wrappedKey = WebSQLTransaction.wrapKeyIfNeeded(key);
                var tableName = "\"" + table.tableName + "\"";
                var primaryKeys = table.attributes.filter(function (att) { return att.isPrimaryKey; });
                var argList = tslib_1.__spreadArray([], wrappedKey);
                var keysClause = primaryKeys.map(function (att) { return "\"" + att.columnName + "\" = ?"; }).join(" AND ");
                var onSuccess = function (transaction, resultSet) {
                    _this.trace("Deleted record with key '" + key + "' from table '" + table.tableName + "' successfully");
                    resolve();
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error deleting record with key '" + key + "' from table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("DELETE FROM " + tableName + " WHERE " + (keysClause === "" ? "ROWID = ?" : keysClause), argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.removeAllRecords = function (table) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Deleting all records from table '" + table.tableName + "'");
                if (_this.mode < WebSQLTransactionMode_1.default.Write) {
                    var error = new Error(WRITE_TRANSACTION_REQUIRED_ERROR);
                    _this.trace("Error deleting all records from table '" + table.tableName + "'", error);
                    return reject(error);
                }
                var tableName = "\"" + table.tableName + "\"";
                var onSuccess = function (transaction, resultSet) {
                    _this.trace("Deleted all records from table '" + table.tableName + "' successfully");
                    resolve();
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error deleting all records from table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("DELETE FROM " + tableName, null, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.findRecords = function (table, criteria) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Searching for all records on table '" + table.tableName + "' that match the specified criteria");
                var hasPrimaryKey = TableInfoUtils.getIdentifierColumns(table).length > 0 ? true : false;
                var columnNames = (!hasPrimaryKey ? "\"rowid\" AS \"ROWID\", " : "") +
                    table.attributes.map(function (att) { return "\"" + att.columnName + "\""; }).join(", ");
                var tableName = "\"" + table.tableName + "\"";
                var criteriaClauses = "";
                var argList = [];
                for (var key in criteria) {
                    if (criteria.hasOwnProperty(key)) {
                        var logic = criteriaClauses.length > 0 ? " AND " : "";
                        criteriaClauses += logic + "\"" + TableInfoUtils.formatObjectName(key) + "\" = ?";
                        argList.push(criteria[key]);
                    }
                }
                var onSuccess = function (transaction, resultSet) {
                    _this.trace("Search on table '" + table.tableName + "' found " + resultSet.rows.length + " record(s) matching the specified criteria");
                    var dbResult = WebSQLTransaction.convertSQLResultSetToDatabaseRecord(table, resultSet);
                    resolve(dbResult);
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error searching for records on table '" + table.tableName + "'" + NEWLINE + error, error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("SELECT " + columnNames + " FROM " + tableName + " WHERE " + criteriaClauses, argList, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.getAllRecords = function (table) {
            var _this = this;
            return new Promise(function (resolve, reject) {
                _this.trace("Getting all records on table '" + table.tableName + "'");
                var hasPrimaryKey = TableInfoUtils.getIdentifierColumns(table).length > 0 ? true : false;
                var columnNames = (!hasPrimaryKey ? "\"rowid\" AS \"ROWID\", " : "") +
                    table.attributes.map(function (att) { return "\"" + att.columnName + "\""; }).join(", ");
                var tableName = "\"" + table.tableName + "\"";
                var onSuccess = function (transaction, resultSet) {
                    _this.trace("Retrieved all records from table '" + table.tableName + "' successfully");
                    var dbResult = WebSQLTransaction.convertSQLResultSetToDatabaseRecord(table, resultSet);
                    resolve(dbResult);
                };
                var onError = function (transaction, sqlError) {
                    var error = new Error(sqlError.message.toString());
                    _this.trace("Error retrieving all records from table '" + table.tableName + "'", error);
                    reject(error);
                    return false;
                };
                _this.transaction.executeSql("SELECT " + columnNames + " FROM " + tableName, null, onSuccess, onError);
            });
        };
        WebSQLTransaction.prototype.trace = function (message, error) {
            if (this.allowTraces) {
                Logger.trace(this, message + " on transaction " + this.transactionId + (error ? ": " + error.message : ""));
            }
        };
        WebSQLTransaction.wrapKeyIfNeeded = function (key) {
            return key instanceof Array ? key : [key];
        };
        WebSQLTransaction.convertSQLResultSetToDatabaseRecord = function (table, resultSet) {
            var hasPrimaryKey = TableInfoUtils.getIdentifierColumns(table).length > 0 ? true : false;
            var dbResult = [];
            if (resultSet.rows.length > 0) {
                var _loop_3 = function (j) {
                    var row = resultSet.rows.item(j);
                    var tempRecord = {};
                    if (!hasPrimaryKey) {
                        tempRecord["$KEY"] = row.ROWID;
                    }
                    table.attributes.map(function (att) {
                        tempRecord[att.columnName] = row[att.columnName];
                    });
                    dbResult.push(tempRecord);
                };
                for (var j = 0; j < resultSet.rows.length; j++) {
                    _loop_3(j);
                }
            }
            return dbResult;
        };
        WebSQLTransaction.concatenateMultipleTimes = function (repetition, numTimes, separator) {
            return Array.apply(null, new Array(numTimes))
                .map(function () { return repetition; })
                .join("" + separator);
        };
        WebSQLTransaction.MaxQueryParameters = 500;
        WebSQLTransaction.lastTransactionId = 0;
        return WebSQLTransaction;
    }());
    exports.default = WebSQLTransaction;
});
//# sourceMappingURL=WebSQLTransaction.js.map;
